import React, { useState } from "react";
import { Button, TextField } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import DropDownComponent from "../DropDownComponent";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { showCustomToast } from "../Misc/CustomToast";
import UploadedFilesComponent from "../Updloader/UploadedFilesComponent";
import UploaderDocForNote from "../Updloader/UploaderDocForNote";
import "./EditFamilyNoteRedesign.css";

function EditFamilyNote(props) {
  const { currentUser } = useAuth();

  const [quickNoteText, setQuickNoteText] = useState(props.note);
  const [effectiveDateTime, setEffectiveDateTime] = useState(
    props.effectiveDate
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fileLinks, setFileLinks] = useState(props.fileLinks || []);
  const [relationshipFamilyNote, setRelationshipFamilyNote] = useState(
    props.relationshipFamilyNote || ""
  );
  const [files, setFiles] = useState([]);
  const [hasChanged, setHasChanged] = useState(false);

  const handleDateTimeChange = (event) => {
    setEffectiveDateTime(event);
    setHasChanged(true);
  };

  const handleRelationshipFamilyNote = (event) => {
    setRelationshipFamilyNote(event);
    setHasChanged(true);
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files.length <= 10) {
      setFiles(event.target.files);
      setHasChanged(true);
    } else {
      showCustomToast("You can only upload up to 10 files.", "error");
    }
  };

  const deleteFile = async (fileUrl, fileName, noteId) => {
    const storage = getStorage();
    const filePath = `Documents/familyNotes/${noteId}/${fileName}`;
    const storageRef = ref(storage, filePath);

    try {
      await deleteObject(storageRef);
      const newFileLinks = fileLinks.filter((file) => file.url !== fileUrl);
      setFileLinks(newFileLinks);
      const noteRef = doc(db, "notes", noteId);

      await updateDoc(noteRef, {
        fileUrls: newFileLinks.map((file) => ({
          url: file.url,
          name: file.name,
        })),
      });

      showCustomToast("File successfully deleted!", "success");
    } catch (error) {
      console.log("Error deleting file:", error);
      showCustomToast("Error deleting file", "error");
    }
  };

  const uploadFilesAndGetUrls = async (noteId, filesToUpload) => {
    const storage = getStorage();
    const uploadedFileUrls = [];

    for (const file of filesToUpload) {
      const filePath = `Documents/familyNotes/${noteId}/${file.name}`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);
      uploadedFileUrls.push({ url: fileUrl, name: file.name });
    }

    return uploadedFileUrls;
  };

  const handleUpdateFamilyNote = async (
    note,
    noteId,
    effectiveDateTime,
    relationship,
    newFiles
  ) => {
    if (quickNoteText && effectiveDateTime && relationshipFamilyNote) {
      setIsLoading(true);
      try {
        const noteRef = doc(db, "notes", noteId);
        const newFileUrls = await uploadFilesAndGetUrls(noteId, newFiles);
        const updatedFileLinks = [...fileLinks, ...newFileUrls];

        const noteData = {
          noteContent: note,
          updateAt: serverTimestamp(),
          doneBy: currentUser.displayName,
          effectiveDateTime: effectiveDateTime,
          relationShipFamilyNote: relationship,
          fileUrls: updatedFileLinks,
        };

        await updateDoc(noteRef, noteData);

        showCustomToast("Note successfully updated!", "success");

        setIsLoading(false);
        props.closemodal();
      } catch (error) {
        console.log("error updating note :", error);
        showCustomToast("Problem when updating note", "error");
        setIsLoading(false);
      }
    } else {
      showCustomToast("Some mandatory fields are empty. Please check.", "error");
    }
  };

  const handleChange = (event) => {
    setQuickNoteText(event.target.value);
    setHasChanged(true);
  };

  return (
    <div className="edit-family-note-modal">
      <div className="edit-family-note-header">
        <div>
          <h2>Edit family note</h2>
          <p>Update the family note, relationship, date, and attachments.</p>
        </div>
        <button
          type="button"
          className="edit-family-note-close"
          onClick={props.closemodal}
          aria-label="Close edit family note modal"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="edit-family-note-body">
        <section className="edit-family-note-section">
          <div className="edit-family-note-section-title">
            <span>Note details</span>
          </div>

          <div className="edit-family-note-textarea">
            <div className="edit-family-note-label">
              Note content <span>*</span>
            </div>
            <TextField
              name="familyNote"
              value={quickNoteText}
              onChange={handleChange}
              placeholder="Type family note here"
              multiline
              rows={10}
              fullWidth
            />
          </div>

          <div className="edit-family-note-grid">
            <DatePickerWithTimeComponent
              title="Effective date and time"
              value={effectiveDateTime}
              onChange={handleDateTimeChange}
            />
            <DropDownComponent
              title="Family note relationship"
              api="livingWith"
              value={relationshipFamilyNote}
              onChange={handleRelationshipFamilyNote}
            />
          </div>
        </section>

        <section className="edit-family-note-section">
          <div className="edit-family-note-section-title">
            <span>Attachments</span>
            <strong>{fileLinks.length + files.length} files</strong>
          </div>

          <div className="edit-family-note-uploader">
            <UploaderDocForNote
              handleFileChange={handleFileChange}
              files={files}
            />
            <p>Max 10 files per update.</p>
          </div>

          <div className="edit-family-note-files">
            <UploadedFilesComponent
              fileLinks={fileLinks}
              deleteFile={deleteFile}
              noteId={props.noteId}
            />
          </div>
        </section>
      </div>

      <div className="edit-family-note-footer">
        <Button
          variant="outlined"
          className="edit-family-note-cancel"
          onClick={props.closemodal}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="edit-family-note-save"
          disabled={!hasChanged || isLoading}
          onClick={() =>
            handleUpdateFamilyNote(
              quickNoteText,
              props.noteId,
              effectiveDateTime,
              relationshipFamilyNote,
              files
            )
          }
        >
          {isLoading ? "Updating..." : "Update note"}
        </Button>
      </div>
    </div>
  );
}

export default EditFamilyNote;
